"use client";

import {
  Alert,
  AlertContent,
  AlertDescription,
  AlertIcon,
  AlertTitle,
} from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  formatBytes,
  useFileUpload,
  type FileWithPreview,
} from "@/hooks/use-file-upload";
import { cn } from "@/lib/utils";
import { IdCard, TriangleAlert, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

interface AvatarUploadProps {
  maxSize?: number;
  className?: string;
  onFileChange?: (file: FileWithPreview | null) => void;
  defaultAvatar?: string;
  value?: File | string | null; // Nuova prop per il valore controllato
  name?: string;
  label?: string;
}

export default function AvatarUpload({
  maxSize = 2 * 1024 * 1024, // 2MB
  className,
  onFileChange,
  defaultAvatar,
  value,
  label,
  name = "avatar",
}: AvatarUploadProps) {
  const [
    { files, isDragging, errors },
    {
      removeFile,
      handleDragEnter,
      handleDragLeave,
      handleDragOver,
      handleDrop,
      openFileDialog,
      getInputProps,
      handleFileChange,
    },
  ] = useFileUpload({
    maxFiles: 1,
    maxSize,
    accept: "image/*",
    multiple: false,
    onFilesChange: (files) => {
      onFileChange?.(files[0] || null);
    },
  });

  const currentFile = files[0];
  const [previewUrl, setPreviewUrl] = useState(
    currentFile?.preview || defaultAvatar,
  );

  const hiddenInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!value && currentFile) {
      removeFile(currentFile.id);
    }
    if (value instanceof Blob) {
      setPreviewUrl(URL.createObjectURL(value));
    }
  }, [value]);

  useEffect(() => {
    if (hiddenInputRef.current) {
      if (currentFile) {
        hiddenInputRef.current.value = currentFile.file.name;
      } else {
        hiddenInputRef.current.value = "";
      }
    }
  }, [currentFile]);

  const handleRemove = () => {
    if (currentFile) {
      removeFile(currentFile.id);
      onFileChange?.(null);
    }
  };

  return (
    <div className={cn("flex flex-col items-center gap-4", className)}>
      <input
        ref={hiddenInputRef}
        type="hidden"
        name={name}
        value={currentFile ? currentFile.file.name : ""}
      />

      {previewUrl && (
        <input type="hidden" name={`${name}_preview`} value={previewUrl} />
      )}

      <div className="relative">
        <div
          className={cn(
            "group/avatar relative h-40 aspect-video cursor-pointer overflow-hidden rounded-sm border border-dashed transition-colors",
            isDragging
              ? "border-primary bg-primary/5"
              : "border-muted-foreground/25 hover:border-muted-foreground/20",
            previewUrl && "border-solid",
          )}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={openFileDialog}
          onInput={handleFileChange}
        >
          <input {...getInputProps()} className="sr-only" />

          {previewUrl ? (
            <img
              src={previewUrl}
              alt="Avatar"
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <IdCard className="size-6 text-muted-foreground" />
            </div>
          )}
        </div>

        {currentFile && (
          <Button
            size="icon"
            variant="outline"
            onClick={handleRemove}
            className="size-6 absolute -end-2 -top-2 rounded-full"
            aria-label="Remove avatar"
            type="button"
          >
            <X className="size-3.5" />
          </Button>
        )}
      </div>

      <div className="text-center space-y-0.5">
        <p className="text-sm font-medium">
          {currentFile ? "File caricato" : (label ?? "")}
        </p>
        <p className="text-xs text-muted-foreground">
          PNG, JPG, PDF up to {formatBytes(maxSize)}
        </p>
      </div>

      {/* Error Messages */}
      {errors.length > 0 && (
        <Alert variant="destructive" appearance="light" className="mt-5">
          <AlertIcon>
            <TriangleAlert />
          </AlertIcon>
          <AlertContent>
            <AlertTitle>File upload error(s)</AlertTitle>
            <AlertDescription>
              {errors.map((error, index) => (
                <p key={index} className="last:mb-0">
                  {error}
                </p>
              ))}
            </AlertDescription>
          </AlertContent>
        </Alert>
      )}
    </div>
  );
}
